import DOMPurify from "dompurify";

/**
 * Input sanitization utility to prevent XSS attacks
 * Uses DOMPurify to sanitize HTML content and user inputs
 */

export interface SanitizeOptions {
  allowHtml?: boolean;
  allowedTags?: string[];
  allowedAttributes?: { [key: string]: string[] };
  stripTags?: boolean;
}

class InputSanitizer {
  /**
   * Sanitize HTML content
   * Removes dangerous tags and attributes while preserving safe formatting
   */
  public sanitizeHtml(dirty: string, options: SanitizeOptions = {}): string {
    const config: Parameters<typeof DOMPurify.sanitize>[1] = {};

    if (options.allowedTags) {
      config.ALLOWED_TAGS = options.allowedTags;
    }

    if (options.allowedAttributes) {
      config.ALLOWED_ATTR = Object.values(options.allowedAttributes).flat();
    }

    if (options.stripTags) {
      config.ALLOWED_TAGS = [];
    }

    return DOMPurify.sanitize(dirty, config);
  }

  /**
   * Sanitize plain text input
   * Strips all HTML tags and entities
   */
  public sanitizeText(dirty: string): string {
    return DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true,
    });
  }

  /**
   * Sanitize email input
   * Basic email format validation and XSS prevention
   */
  public sanitizeEmail(email: string): string {
    // Remove all HTML tags
    const cleaned = this.sanitizeText(email);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleaned)) {
      return "";
    }

    return cleaned.toLowerCase().trim();
  }

  /**
   * Sanitize phone number input
   * Removes non-numeric characters except +, -, (, )
   */
  public sanitizePhone(phone: string): string {
    const cleaned = this.sanitizeText(phone);
    return cleaned.replace(/[^0-9+\-() ]/g, "");
  }

  /**
   * Sanitize URL input
   * Ensures URL is safe and uses allowed protocols
   */
  public sanitizeUrl(
    url: string,
    allowedProtocols: string[] = ["http", "https"],
  ): string {
    try {
      const cleaned = this.sanitizeText(url);
      const urlObj = new URL(cleaned);

      if (!allowedProtocols.includes(urlObj.protocol.replace(":", ""))) {
        return "";
      }

      return urlObj.href;
    } catch {
      return "";
    }
  }

  /**
   * Sanitize numeric input
   * Ensures input is a valid number
   */
  public sanitizeNumber(input: string | number): number | null {
    const cleaned = String(input).replace(/[^0-9.-]/g, "");
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  }

  /**
   * Sanitize date input
   * Ensures input is a valid date
   */
  public sanitizeDate(input: string): string {
    const cleaned = this.sanitizeText(input);
    const date = new Date(cleaned);
    return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
  }

  /**
   * Sanitize file name
   * Removes path traversal attempts and dangerous characters
   */
  public sanitizeFileName(fileName: string): string {
    const cleaned = this.sanitizeText(fileName);

    // Remove path traversal attempts
    let safe = cleaned.replace(/\.\./g, "");

    // Remove dangerous characters
    safe = safe.replace(/[^a-zA-Z0-9._-]/g, "_");

    // Limit length
    if (safe.length > 255) {
      safe = safe.substring(0, 255);
    }

    return safe;
  }

  /**
   * Sanitize object with multiple fields
   * Applies appropriate sanitization based on field type
   */
  public sanitizeObject<T extends Record<string, unknown>>(
    obj: T,
    fieldConfig: Partial<
      Record<
        keyof T,
        | "text"
        | "html"
        | "email"
        | "phone"
        | "url"
        | "number"
        | "date"
        | "fileName"
      >
    >,
  ): T {
    const sanitized = { ...obj };

    Object.keys(fieldConfig).forEach((key) => {
      const field = key as keyof T;
      const type = fieldConfig[field];
      const value = obj[field];

      if (value === undefined || value === null) {
        return;
      }

      switch (type) {
        case "text":
          sanitized[field] = this.sanitizeText(String(value)) as T[keyof T];
          break;
        case "html":
          sanitized[field] = this.sanitizeHtml(String(value)) as T[keyof T];
          break;
        case "email":
          sanitized[field] = this.sanitizeEmail(String(value)) as T[keyof T];
          break;
        case "phone":
          sanitized[field] = this.sanitizePhone(String(value)) as T[keyof T];
          break;
        case "url":
          sanitized[field] = this.sanitizeUrl(String(value)) as T[keyof T];
          break;
        case "number":
          sanitized[field] = this.sanitizeNumber(String(value)) as T[keyof T];
          break;
        case "date":
          sanitized[field] = this.sanitizeDate(String(value)) as T[keyof T];
          break;
        case "fileName":
          sanitized[field] = this.sanitizeFileName(String(value)) as T[keyof T];
          break;
      }
    });

    return sanitized;
  }

  /**
   * Sanitize form data
   * Common sanitization for form submissions
   */
  public sanitizeFormData(
    formData: Record<string, unknown>,
  ): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    Object.keys(formData).forEach((key) => {
      const value = formData[key];

      if (value === undefined || value === null) {
        sanitized[key] = value;
        return;
      }

      // Auto-detect field type based on key name
      if (key.includes("email") || key === "email") {
        sanitized[key] = this.sanitizeEmail(String(value));
      } else if (key.includes("phone") || key.includes("Phone")) {
        sanitized[key] = this.sanitizePhone(String(value));
      } else if (key.includes("url") || key.includes("website")) {
        sanitized[key] = this.sanitizeUrl(String(value));
      } else if (key.includes("date") || key.includes("Date")) {
        sanitized[key] = this.sanitizeDate(String(value));
      } else if (typeof value === "number") {
        sanitized[key] = this.sanitizeNumber(value);
      } else {
        sanitized[key] = this.sanitizeText(String(value));
      }
    });

    return sanitized;
  }

  /**
   * Check if input contains potential XSS
   * Returns true if suspicious patterns detected
   */
  public containsXSS(input: string): boolean {
    const xssPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i, // event handlers like onclick=
      /<iframe/i,
      /<embed/i,
      /<object/i,
      /vbscript:/i,
      /data:text\/html/i,
    ];

    return xssPatterns.some((pattern) => pattern.test(input));
  }

  /**
   * Check if input contains SQL injection patterns
   * Returns true if suspicious patterns detected
   */
  public containsSQLInjection(input: string): boolean {
    const sqlPatterns = [
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE)\b)/i,
      /(--|\*|;|'|")/,
      /(\bOR\b.*=.*)/i,
      /(\bAND\b.*=.*)/i,
      /(\bUNION\b.*\bSELECT\b)/i,
    ];

    return sqlPatterns.some((pattern) => pattern.test(input));
  }

  /**
   * Validate and sanitize all inputs
   * Throws error if dangerous patterns detected
   */
  public validateAndSanitize(
    input: string,
    type: "text" | "html" = "text",
  ): string {
    if (this.containsXSS(input)) {
      throw new Error("Potential XSS detected in input");
    }

    if (this.containsSQLInjection(input)) {
      throw new Error("Potential SQL injection detected in input");
    }

    return type === "html"
      ? this.sanitizeHtml(input)
      : this.sanitizeText(input);
  }
}

// Export singleton instance
export const sanitizer = new InputSanitizer();

// Export class for testing
export default InputSanitizer;
